import { useState, useEffect } from 'react';
import { DEFAULT_NATIVITY_OPTIONS } from '../constants/nativityOptions';

interface SystemConfig {
  site_name: string;
  site_address: string;
  site_email: string;
  site_phone: string;
  site_tagline: string;
  facebook_url: string;
  linkedin_url: string;
  instagram_url: string;
  site_logo?: string;
  nativity_options: string[];
}

interface UseSystemConfigResult {
  config: SystemConfig | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const useSystemConfig = (): UseSystemConfigResult => {
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/system-config/public`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          // Convert null values to empty strings and provide defaults
          const cleanData: SystemConfig = {
            site_name: data.data.site_name || 'Siruvani Foundation',
            site_address: data.data.site_address || 'TTK Road, Alwarpet, Chennai, India',
            site_email: data.data.site_email || 'info@siruvani.com',
            site_phone: data.data.site_phone || '+91 98766 54321',
            site_tagline: data.data.site_tagline || 'Bringing Coimbatore to Chennai since 2012',
            facebook_url: data.data.facebook_url || 'https://facebook.com/siruvanifoundation',
            linkedin_url: data.data.linkedin_url || 'https://linkedin.com/company/siruvanifoundation',
            instagram_url: data.data.instagram_url || 'https://instagram.com/siruvanifoundation',
            site_logo: data.data.site_logo || '',
            nativity_options: Array.isArray(data.data.nativity_options) && data.data.nativity_options.length > 0
              ? data.data.nativity_options
              : DEFAULT_NATIVITY_OPTIONS
          };
          setConfig(cleanData);
        } else {
          // Use default values if no config found
          setConfig({
            site_name: 'Siruvani Foundation',
            site_address: 'TTK Road, Alwarpet, Chennai, India',
            site_email: 'info@siruvani.com',
            site_phone: '+91 98766 54321',
            site_tagline: 'Bringing CNET to Chennai since 2012',
            facebook_url: 'https://facebook.com/siruvanifoundation',
            linkedin_url: 'https://linkedin.com/company/siruvanifoundation',
            instagram_url: 'https://instagram.com/siruvanifoundation',
            site_logo: '',
            nativity_options: DEFAULT_NATIVITY_OPTIONS
          });
        }
      } else {
        // Use default values if API fails
        setConfig({
          site_name: 'Siruvani Foundation',
          site_address: 'TTK Road, Alwarpet, Chennai, India',
          site_email: 'info@siruvani.com',
          site_phone: '+91 98766 54321',
          site_tagline: 'Bringing CNET to Chennai since 2012',
          facebook_url: 'https://facebook.com/siruvanifoundation',
          linkedin_url: 'https://linkedin.com/company/siruvanifoundation',
          instagram_url: 'https://instagram.com/siruvanifoundation',
          site_logo: '',
          nativity_options: DEFAULT_NATIVITY_OPTIONS
        });
      }
    } catch (err) {

      setError('Failed to load system configuration');
      // Use default values on error
      setConfig({
        site_name: 'Siruvani Foundation',
        site_address: 'TTK Road, Alwarpet, Chennai, India',
        site_email: 'info@siruvani.com',
        site_phone: '+91 98766 54321',
        site_tagline: 'Bringing CNET to Chennai since 2012',
        facebook_url: 'https://facebook.com/siruvanifoundation',
        linkedin_url: 'https://linkedin.com/company/siruvanifoundation',
        instagram_url: 'https://instagram.com/siruvanifoundation',
        site_logo: '',
        nativity_options: DEFAULT_NATIVITY_OPTIONS
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  return {
    config,
    loading,
    error,
    refetch: fetchConfig
  };
};

export default useSystemConfig;
